import React, { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import { Button, Copy01, Edit01, HelpCircle, LinkExternal01, Tabs } from "@opendate-app/setlist";
import { toastError } from "../../shared/toastHelper";
import { countOrUndefined } from "../shared/components/CountBadge";
import DetailPageLayout from "../ClassesShared/DetailPageLayout";
import ClassStatusBadge, { upcomingOpensAt } from "../ClassesShared/ClassStatusBadge";
import TintedAlert from "../ClassesShared/TintedAlert";
import ClosedRegistrationPublishModal from "../ClassesShared/ClosedRegistrationPublishModal";
import { formatDateRange, isClassEditable } from "../ClassesShared/classLabels";
import OverviewTab from "./tabs/OverviewTab";
import SessionsTab from "./tabs/SessionsTab";
import SettingsTab from "./tabs/SettingsTab";
import RegistrationsTab from "./tabs/RegistrationsTab";
import WaitlistTab from "./tabs/WaitlistTab";
import PromotionsTab from "./tabs/PromotionsTab";
import "../ClassesShared/classesShared.css";

const TAB_PARAM = "tab";
const TAB_KEYS = ["overview", "sessions", "registrations", "waitlist", "promotions", "settings"];

export const TAB_HELP_ARTICLES = {
  sessions: { path: "ticketing/check-in/class-attendance", label: "How class attendance works" },
  registrations: { path: "ticketing/refunds-transfers/class-cancellations", label: "Cancellations and attendee emails" },
  waitlist: { path: "ticketing/refunds-transfers/class-next-session-list", label: "Sold-out classes and the waitlist" },
  promotions: { path: "ticketing/promo-codes/class-promo-codes", label: "How class promo codes work" },
  settings: { path: "ticketing/refunds-transfers/class-cancellations", label: "Refund window and canceling a class" },
};

function initialTab() {
  const requested = new URLSearchParams(window.location.search).get(TAB_PARAM);
  return TAB_KEYS.includes(requested) ? requested : "overview";
}

export function headerMeta(classInstance) {
  const sessions = classInstance.sessions_count;
  return [
    classInstance.starts_on ? formatDateRange(classInstance.starts_on, classInstance.ends_on, classInstance.time_zone) : null,
    sessions != null ? `${sessions} session${sessions === 1 ? "" : "s"}` : null,
    classInstance.place_label || null,
    (classInstance.instructors || []).map((instructor) => instructor.name).join(", ") || null,
  ].filter(Boolean).join(" · ");
}

function ClassInstanceShowApp(props) {
  const { classInstance, canManage, currencyCode, paths, breadcrumb, csrfToken, publicPermalink, publicUrl, publiclyViewable, helpCenterEnabled } = props;
  const [active, setActive] = useState(initialTab);
  const [working, setWorking] = useState(false);
  const [rosterVersion, setRosterVersion] = useState(0);
  const [registrationActions, setRegistrationActions] = useState(null);
  const [closedPublishReason, setClosedPublishReason] = useState(null);
  const versioned = (path) => (rosterVersion ? `${path}?roster=${rosterVersion}` : path);

  const [ci, setCi] = useState(classInstance);
  const canceled = ci.status === "canceled";

  useEffect(() => {
    if (!rosterVersion) return undefined;
    let current = true;
    axios.get(paths.overviewData, { params: { format: "json" } })
      .then((resp) => { if (current) setCi(resp.data.header); })
      .catch(() => { if (current) toastError("Could not refresh the class details. Reload to see them."); });
    return () => { current = false; };
  }, [rosterVersion, paths.overviewData]);

  const selectTab = (key) => {
    setActive(key);
    const url = new URL(window.location.href);
    url.searchParams.set(TAB_PARAM, key);
    window.history.replaceState(window.history.state, "", url);
  };

  const publish = async () => {
    setWorking(true);
    try {
      await axios.post(paths.publish, {}, { headers: { "X-CSRF-Token": csrfToken } });
      window.location.reload();
    } catch (e) {
      setWorking(false);
      setClosedPublishReason(null);
      toastError(e.response?.data?.errors?.base?.[0] || "Action failed.");
    }
  };

  const onPublish = async () => {
    setWorking(true);
    const current = await axios.get(paths.overviewData).then((resp) => resp.data.classInstance).catch(() => ci);
    setWorking(false);
    if (current.registration_closed_reason) setClosedPublishReason(current.registration_closed_reason);
    else publish();
  };

  const onDuplicate = async () => {
    setWorking(true);
    try {
      const resp = await axios.post(paths.duplicate, {}, { headers: { "X-CSRF-Token": csrfToken } });
      window.location.href = `${paths.index}/${resp.data.id}/edit`;
    } catch (e) {
      setWorking(false);
      toastError("Could not duplicate class.");
    }
  };

  const actions = (canManage || publicPermalink) && (
    <div className="d-flex align-items-center flex-wrap classes-gap-8">
      {publicPermalink && (
        <Button as="a" variant="secondary" icon={LinkExternal01} href={publicPermalink} target="_blank" rel="noreferrer">
          {publiclyViewable ? "View Public Page" : "Preview Public Page"}
        </Button>
      )}
      {canManage && (
        <>
          <Button variant="secondary" icon={Copy01} onClick={onDuplicate} disabled={working}>Duplicate</Button>
          {isClassEditable(ci.status) && (
            <Button as="a" variant={ci.status === "draft" ? "secondary" : "primary"} icon={Edit01} href={paths.edit}>Edit</Button>
          )}
          {ci.status === "draft" && <Button variant="primary" onClick={onPublish} disabled={working}>Publish</Button>}
        </>
      )}
    </div>
  );

  const tabItems = [
    { value: "overview", label: "Overview" },
    { value: "sessions", label: "Sessions", count: countOrUndefined(ci.sessions_count) },
    { value: "registrations", label: "Registrations", count: countOrUndefined(ci.registered_count) },
    { value: "waitlist", label: "Waitlist", count: countOrUndefined(ci.waitlist_count) },
    { value: "promotions", label: "Promotions" },
    { value: "settings", label: "Settings" },
  ];

  const tabs = (
    <Tabs
      aria-label="Class detail sections"
      collapseBelow={0}
      overflow
      value={active}
      onChange={selectTab}
      items={tabItems}
    />
  );

  return (
    <>
      <DetailPageLayout
        breadcrumbs={breadcrumb}
        title={ci.name}
        badges={<ClassStatusBadge status={ci.status} size="md" opensAt={upcomingOpensAt(ci)} timeZone={ci.time_zone} />}
        supportingText={headerMeta(ci)}
        actions={active === "registrations" && registrationActions ? registrationActions : actions}
        tabs={tabs}
      >
        {canceled && (
          <div className="mb-4">
            <TintedAlert
              color="error"
              title="This class was canceled"
              description={ci.cancellation_reason ? `Reason: ${ci.cancellation_reason}` : "Nobody can register for it any more."}
            />
          </div>
        )}
        {helpCenterEnabled && TAB_HELP_ARTICLES[active] && (
          <div className="d-flex justify-content-end mb-2">
            <Button as="a" variant="link-color" size="sm" icon={HelpCircle} href={`/user_manual#${TAB_HELP_ARTICLES[active].path}`} target="_blank" rel="noreferrer">
              {TAB_HELP_ARTICLES[active].label}
            </Button>
          </div>
        )}
        <div role="tabpanel" aria-labelledby="class-tab-panel-heading" tabIndex={0}>
          <h2 id="class-tab-panel-heading" className="sr-only">{tabItems.find((item) => item.value === active).label}</h2>
          {active === "overview" && (
            <OverviewTab
              url={versioned(paths.overviewData)}
              currencyCode={currencyCode}
              timeZone={ci.time_zone}
              publicUrl={publicUrl}
              publiclyViewable={publiclyViewable}
              registrationsPath={paths.registrations}
              onViewRegistrations={() => selectTab("registrations")}
            />
          )}
          {active === "sessions" && <SessionsTab url={versioned(paths.sessionsData)} csrfToken={csrfToken} format={ci.format} editable={isClassEditable(ci.status)} onChange={() => setRosterVersion((version) => version + 1)} />}
          {active === "registrations" && (
            <RegistrationsTab
              registrationsPath={paths.registrations}
              complimentaryPath={paths.complimentaryRegistrations}
              csrfToken={csrfToken}
              currencyCode={currencyCode}
              timeZone={ci.time_zone}
              onChange={() => setRosterVersion((version) => version + 1)}
              onHeaderActions={setRegistrationActions}
            />
          )}
          {active === "waitlist" && (
            <WaitlistTab
              waitlistPath={paths.waitlistEntries}
              csrfToken={csrfToken}
              timeZone={ci.time_zone}
              onChange={() => setRosterVersion((version) => version + 1)}
            />
          )}
          {active === "promotions" && (
            <PromotionsTab promoCodesPath={paths.promoCodes} csrfToken={csrfToken} canManage={canManage} currencyCode={currencyCode} timeZone={ci.time_zone} />
          )}
          {active === "settings" && <SettingsTab url={versioned(paths.settingsData)} cancelPath={paths.cancel} editPath={canManage ? paths.edit : null} canManage={canManage} csrfToken={csrfToken} timeZone={ci.time_zone} />}
        </div>
      </DetailPageLayout>
      {closedPublishReason && (
        <ClosedRegistrationPublishModal
          reason={closedPublishReason}
          working={working}
          onConfirm={publish}
          onClose={() => setClosedPublishReason(null)}
        />
      )}
      <ToastContainer icon={false} />
    </>
  );
}

ClassInstanceShowApp.propTypes = {
  classInstance: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    status: PropTypes.string,
    format: PropTypes.string,
    capacity: PropTypes.number,
    registered_count: PropTypes.number,
    registration_closed_reason: PropTypes.oneOf(["first_session", "close_time"]),
    sessions_count: PropTypes.number,
    waitlist_count: PropTypes.number,
    starts_on: PropTypes.string,
    ends_on: PropTypes.string,
    time_zone: PropTypes.string,
    place_label: PropTypes.string,
    instructors: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    cancellation_reason: PropTypes.string,
  }).isRequired,
  canManage: PropTypes.bool,
  helpCenterEnabled: PropTypes.bool,
  currencyCode: PropTypes.string,
  paths: PropTypes.shape({
    index: PropTypes.string,
    edit: PropTypes.string,
    overviewData: PropTypes.string,
    sessionsData: PropTypes.string,
    settingsData: PropTypes.string,
    publish: PropTypes.string,
    cancel: PropTypes.string,
    duplicate: PropTypes.string,
    registrations: PropTypes.string,
    complimentaryRegistrations: PropTypes.string,
    waitlistEntries: PropTypes.string,
    promoCodes: PropTypes.string,
  }).isRequired,
  breadcrumb: PropTypes.array,
  csrfToken: PropTypes.string.isRequired,
  publicPermalink: PropTypes.string,
  publicUrl: PropTypes.string,
  publiclyViewable: PropTypes.bool,
};

export default ClassInstanceShowApp;
